import type { PostHog } from "posthog-js";
import { isAnalyticsHost } from "@/lib/analytics/analyticsHost";
import { captureException } from "@/lib/sentry/captureClient";
import { SentryTag } from "@/lib/sentry/tags";

let posthogPromise: Promise<PostHog> | null = null;

/**
 * Downloads and initializes PostHog once, outside the initial page bundle.
 * Returns null where analytics is off: on the server, on localhost, or
 * without a project key.
 */
export function loadPosthog(): Promise<PostHog> | null {
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!key || !isAnalyticsHost()) return null;
  posthogPromise ??= import("posthog-js").then(({ default: posthog }) => {
    posthog.init(key, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
      defaults: "2025-05-24",
    });
    return posthog;
  });
  return posthogPromise;
}

/** Runs `use` once PostHog is loaded; load or call errors go to Sentry. */
export function withPosthog(
  source: string,
  use: (posthog: PostHog) => void,
): void {
  loadPosthog()
    ?.then(use)
    .catch((error: unknown) => {
      captureException(error, { tags: { [SentryTag.Source]: source } });
    });
}
